import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const RecommendationGrid = ({ recommendations }) => {
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return {
      day: date?.getDate(),
      month: date?.toLocaleDateString('en-US', { month: 'short' }),
      weekday: date?.toLocaleDateString('en-US', { weekday: 'short' })
    };
  };

  const formatPrice = (price) => {
    return price === 0 ? 'Free' : `$${price}`;
  };

  const RecommendationCard = ({ event }) => {
    const { day, month, weekday } = formatDate(event?.date);

    return (
      <div className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-md transition-smooth group">
        <div className="relative">
          <Image
            src={event?.image}
            alt={event?.title}
            className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
          />
          <div className="absolute top-3 left-3 bg-background/90 backdrop-blur-sm rounded-lg p-2 text-center min-w-[50px]">
            <div className="text-sm font-semibold text-foreground">{day}</div>
            <div className="text-xs text-muted-foreground uppercase">{month}</div>
          </div>
          {event?.isPopular && (
            <div className="absolute top-3 right-3 bg-primary text-primary-foreground px-2 py-1 rounded-full text-xs font-medium">
              Popular
            </div>
          )}
          <div className="absolute bottom-3 left-3 right-3">
            <div className="bg-background/90 backdrop-blur-sm rounded-lg p-2">
              <div className="text-sm font-medium text-foreground">{formatPrice(event?.price)}</div>
            </div>
          </div>
        </div>
        <div className="p-4">
          <div className="flex items-start justify-between mb-2">
            <h3 className="font-semibold text-foreground line-clamp-2 flex-1">
              {event?.title}
            </h3>
            <div className="flex items-center ml-2">
              <Icon name="Star" size={14} className="text-warning fill-current" />
              <span className="text-sm text-muted-foreground ml-1">{event?.rating}</span>
            </div>
          </div>
          
          <div className="space-y-1 mb-3">
            <div className="flex items-center text-sm text-muted-foreground">
              <Icon name="Calendar" size={14} className="mr-2" />
              <span>{weekday}, {event?.time}</span>
            </div>
            <div className="flex items-center text-sm text-muted-foreground">
              <Icon name="MapPin" size={14} className="mr-2" />
              <span className="line-clamp-1">{event?.location}</span>
            </div>
            <div className="flex items-center text-sm text-muted-foreground">
              <Icon name="Users" size={14} className="mr-2" />
              <span>{event?.attendees} attending</span>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-1">
              {event?.tags?.slice(0, 2)?.map((tag, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-accent/20 text-accent-foreground"
                >
                  {tag}
                </span>
              ))}
            </div>
            
            <Button
              variant="default"
              size="sm"
              asChild
            >
              <Link to={`/event-details?id=${event?.id}`}>
                View Event
              </Link>
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-card border border-border rounded-lg">
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Recommended for You</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Events curated based on your interests and booking history
            </p>
          </div>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/event-discovery">
              <span className="mr-2">View All</span>
              <Icon name="ArrowRight" size={14} />
            </Link>
          </Button>
        </div>
      </div>
      <div className="p-4">
        {recommendations?.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {recommendations?.map((event) => (
              <RecommendationCard key={event?.id} event={event} />
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <Icon name="Sparkles" size={48} className="mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">No recommendations yet</h3>
            <p className="text-muted-foreground mb-4">
              Book your first event to get personalized recommendations
            </p>
            <Button variant="default" asChild>
              <Link to="/event-discovery">
                <Icon name="Search" size={16} className="mr-2" />
                Explore Events
              </Link>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RecommendationGrid;